import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { AdminBookingPricePreviewDto } from "../../../models/payment/dto/admin-booking.dto";
import { GameRankedMode, GameType } from "../../../models/game/dto/game.dto";
import { SportType } from "../../../models/court/dto/court.dto";

export type UseAdminBookingPricePreviewParams = {
    organizationId: string;
    userId: string | null;
    courtId: number | null;
    startTime: string | null;
    endTime: string | null;
    sportType: SportType;
    capacity?: number;
    gameType?: GameType;
    rankedMode?: GameRankedMode;
};

/**
 * Live price preview for the admin-booking modal. Only fires when all
 * required fields are populated — keeps the request count down while the
 * operator is still filling out the form.
 */
export const useAdminBookingPricePreview = (
    params: UseAdminBookingPricePreviewParams,
) => {
    const apiClient = useApiClient();

    const enabled =
        Boolean(params.organizationId) &&
        Boolean(params.userId) &&
        Boolean(params.courtId) &&
        Boolean(params.startTime) &&
        Boolean(params.endTime) &&
        Boolean(params.sportType);

    return useQuery({
        queryKey: [
            'adminBookingPricePreview',
            params.organizationId,
            params.userId,
            params.courtId,
            params.startTime,
            params.endTime,
            params.sportType,
            params.capacity,
            params.gameType,
            params.rankedMode,
        ],
        queryFn: () => {
            const search = new URLSearchParams({
                organizationId: params.organizationId,
                userId: params.userId!,
                courtId: String(params.courtId),
                startTime: params.startTime!,
                endTime: params.endTime!,
                sportType: params.sportType,
            });
            if (params.capacity !== undefined) {
                search.set('capacity', String(params.capacity));
            }
            if (params.gameType) {
                search.set('gameType', params.gameType);
            }
            if (params.rankedMode) {
                search.set('rankedMode', params.rankedMode);
            }
            return apiClient.fetchApi<AdminBookingPricePreviewDto, {}>({
                method: 'GET',
                endpoint: `payments/admin-bookings/price?${search.toString()}`,
            });
        },
        enabled,
        staleTime: 30_000,
    });
};
